import React, { useEffect, useState } from 'react'
import { fmt, pickCsvFile } from '../api.js'
import { portal } from '../portalApi.js'
import NoteDrawer from '../components/NoteDrawer.jsx'
import { CompanyPicker, NeedCompany, costGet, useCompany, useMeta, useToast } from './CostCommon.jsx'

// 원가관리 · 입출고. 물류팀에서 받은 수불 내역이 여기로 들어온다.
// 출고 금액은 수불부에서 총평균단가로 평가하므로 여기선 비워 둔다.

const EMPTY = {
  tx_date: '', sku: '', tx_type: '판매출고', qty: '', amount: '',
  location: '', condition: '정상', vintage_year: '', note: '',
}

export default function CostTransactions({ year, onMonthChange, currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [company, setCompany] = useCompany()
  const [meta] = useMeta()
  const [list, setList] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [filter, setFilter] = useState('')
  const [err, setErr] = useState('')
  const [notify, toast] = useToast()

  const load = () => {
    setErr('')
    costGet('/transactions', { year, company }).then(setList).catch((e) => setErr(e.message))
  }
  useEffect(() => { load() }, [year, company])

  const add = async () => {
    try {
      const res = await portal.post('/api/cost/transactions/bulk', [{ ...form, company }])
      if (res.missing.length) setErr(`미등록 품번: ${res.missing.join(', ')}`)
      else {
        setErr('')
        setForm({ ...EMPTY, tx_date: form.tx_date, tx_type: form.tx_type, location: form.location })
        notify('입출고 1건 등록')
      }
      load()
    } catch (e) { setErr(e.message) }
  }

  const importCsv = () => pickCsvFile(async (rows) => {
    try {
      const res = await portal.post('/api/cost/transactions/bulk',
        rows.map((r) => ({ company, ...r })))
      notify(`업로드 ${res.inserted}건 반영${res.missing.length ? `, 미등록 품번 ${res.missing.length}건 제외` : ''}`)
      if (res.missing.length) setErr(`품목 마스터에 없는 품번: ${[...new Set(res.missing)].slice(0, 20).join(', ')}`)
      load()
    } catch (e) { setErr(e.message) }
  })

  const remove = async (id) => {
    try { await portal.del(`/api/cost/transactions/${id}`); load() } catch (e) { setErr(e.message) }
  }

  if (!list || !meta) return err ? <div className="error-box">{err}</div> : <div className="muted">불러오는 중…</div>

  const q = filter.trim().toLowerCase()
  const rows = list.filter((t) =>
    !q || [t.sku, t.name, t.option_name, t.tx_type, t.location, t.note].some((v) => (v || '').toLowerCase().includes(q)))
  const locOptions = [...new Set((meta.locations || [])
    .filter((l) => !company || l.company === company).map((l) => l.name))]

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>{year}년 입출고 내역</h2>
        <NoteDrawer target="transactions" screenKey="cost_transactions"
          context={{ year }} contextLabel={`${year}년`}
          onNavigate={(ctx) => ctx?.year && onMonthChange?.(`${ctx.year}-01`)}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>
      {err && <div className="error-box">{err}</div>}
      <div className="row" style={{ marginBottom: 12 }}>
        <CompanyPicker value={company} onChange={setCompany} meta={meta} />
        <input placeholder="검색 (품번/상품명/유형/창고/비고)" style={{ width: 300 }}
          value={filter} onChange={(e) => setFilter(e.target.value)} />
        <span className="muted">{rows.length}건</span>
      </div>
      {!company && <NeedCompany>통합 화면에서는 두 사업부 내역을 함께 봅니다. 등록·업로드는 사업부를 골라야 합니다.</NeedCompany>}

      <div className="panel">
        <h3>신규 등록</h3>
        <div className="row">
          <input type="date" value={form.tx_date} onChange={(e) => setForm({ ...form, tx_date: e.target.value })} />
          <input placeholder="품번(sku)" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
          <select value={form.tx_type} onChange={(e) => setForm({ ...form, tx_type: e.target.value })}>
            <optgroup label="입고">{meta.inTypes.map((t) => <option key={t}>{t}</option>)}</optgroup>
            <optgroup label="출고">{meta.outTypes.map((t) => <option key={t}>{t}</option>)}</optgroup>
          </select>
          <input placeholder="수량" style={{ width: 80 }} value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} />
          <input placeholder="금액(입고만)" style={{ width: 120 }} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <input placeholder="창고" list="cost-locations" style={{ width: 130 }}
            value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          <datalist id="cost-locations">
            {locOptions.map((v) => <option key={v} value={v} />)}
          </datalist>
          <select value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}>
            {meta.conditions.map((v) => <option key={v}>{v}</option>)}
          </select>
          <input placeholder="입고연도" style={{ width: 90 }} value={form.vintage_year}
            onChange={(e) => setForm({ ...form, vintage_year: e.target.value })} />
          <input placeholder="비고" style={{ width: 150 }} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <button className="btn" onClick={add} disabled={!company || !form.tx_date || !form.sku || !form.qty}>등록</button>
          <button className="btn secondary" onClick={importCsv} disabled={!company}>CSV 업로드</button>
        </div>
        <div className="hint">
          CSV 헤더: <code>tx_date, sku, tx_type, qty, amount, location, condition, vintage_year, note</code> (tx_date는 YYYY-MM-DD).
          출고 금액은 수불부에서 총평균단가로 자동 평가되므로 비워두세요. 매입입고는 공급가(amount)를 넣습니다.
          <br /><b>입고연도</b>는 비워두면 거래일자의 연도로 봅니다. 조정입고로 옛 재고가 되살아난 경우에만 그 해를 적으세요 — 충당금 연령이 그 값으로 잡힙니다.
        </div>
      </div>

      <div className="panel">
        <h3>내역 (최근 2,000건)</h3>
        <table>
          <thead>
            <tr>
              <th>일자</th>
              {!company && <th className="l">사업부</th>}
              <th className="l">품번</th><th className="l">상품명</th><th>유형</th>
              <th>수량</th><th>금액</th><th className="l">창고</th><th>상태</th><th>입고연도</th>
              <th className="l">비고</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={12} className="c muted">내역이 없습니다</td></tr>}
            {rows.slice(0, 800).map((t) => (
              <tr key={t.id}>
                <td className="c">{String(t.tx_date).slice(0, 10)}</td>
                {!company && <td className="l">{t.company}</td>}
                <td className="l">{t.sku}</td>
                <td className="l">{t.name} {t.option_name}</td>
                <td className="c">{t.tx_type}</td>
                <td>{fmt(t.qty)}</td>
                <td>{t.amount === null ? '-' : fmt(t.amount)}</td>
                <td className="l">{t.location}</td>
                <td className="c">{t.condition}</td>
                <td className="c">{t.vintage_year || ''}</td>
                <td className="l">{t.note}</td>
                <td className="c"><button className="btn danger" onClick={() => remove(t.id)}>삭제</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 800 && <div className="hint">상위 800건만 표시 중 — 검색으로 좁혀주세요.</div>}
      </div>
      {toast}
    </>
  )
}
